import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import EnergyFlow from "../components/EnergyFlow";
import { socket, connectSocket } from "../services/socket";
import { listDevices } from "../services/devices";
import {
    FaThermometerHalf,
    FaWifi,
    FaSolarPanel,
    FaHome,
    FaBolt,
    FaPlug,
    FaMicrochip,
    FaTint,
    FaWind,
    FaSun
} from "react-icons/fa";
import "../styles/dashboard.css";

const ACTIVE_KEY = "activeDeviceSerial";

// ---------------------------------------------------------
// Turns an RSSI reading (dBm) into a 0-4 signal bar count
// ---------------------------------------------------------
function signalBars(rssi) {

    if (!rssi) return 0;
    if (rssi >= -55) return 4;
    if (rssi >= -65) return 3;
    if (rssi >= -75) return 2;
    if (rssi >= -85) return 1;

    return 0;

}

export default function Dashboard() {

    // ---------------- Which device this account is viewing ----------------
    const [activeSerial, setActiveSerial] = useState(
        () => localStorage.getItem(ACTIVE_KEY) || ""
    );
    const [checkingDevices, setCheckingDevices] = useState(true);

    // ---------------- Live device + UI state ----------------
    const [device, setDevice] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [deviceStatus, setDeviceStatus] = useState("Offline");
    
    // Today's peaks / energy totals (from /api/stats/today/:serial)
    const [todayStats, setTodayStats] = useState({
        peak_pv: 0,
        peak_load: 0,
        solar_energy_kwh: 0,
        grid_energy_kwh: 0
    });
    // ---------------- Weather ----------------
    const [weather, setWeather] = useState(null);
    // ---------------- Resolve which device to show ----------------
    // If nothing is active yet (fresh login, or the stored serial
    // no longer exists), fall back to the first device on the account.
    useEffect(() => {

        if (activeSerial) {
            setCheckingDevices(false);
            return;
        }

        listDevices()
            .then(res => {

                const list = res.data.devices || [];

                if (list.length > 0) {
                    localStorage.setItem(ACTIVE_KEY, list[0].serial_no);
                    setActiveSerial(list[0].serial_no);
                }

            })
            .catch(console.error)
            .finally(() => setCheckingDevices(false));

    }, [activeSerial]);

    // ---------------- Socket subscriptions (re-bind if the active device changes) ----------------
    useEffect(() => {
connectSocket();
        if (!activeSerial) return;

        setDevice(null);
        setDeviceStatus("Offline");

        function onUpdate(msg) {
            if (msg.serial === activeSerial) setDevice(msg.data);
        }

        function onStatus(msg) {
            if (msg.serial === activeSerial) setDeviceStatus(msg.status);
        }

        socket.on("device:update", onUpdate);
        socket.on("device:status", onStatus);

        return () => {
            socket.off("device:update", onUpdate);
            socket.off("device:status", onStatus);
        };

    }, [activeSerial]);

    // ---------------- Fetch today's stats whenever live data ticks ----------------
    // /api/stats/today/:serial now resolves the serial to the internal
    // device id itself, so this follows whichever device is active.
    useEffect(() => {

        if (!device || !activeSerial) return;

        fetch(
    `${process.env.REACT_APP_API_URL}/api/stats/today/${activeSerial}`
)
            .then(res => res.json())
            .then(setTodayStats)
            .catch(console.error);

    }, [device, activeSerial]);
    // ---------------- Weather ----------------
    useEffect(() => {

        if (!navigator.geolocation) {
            console.error("Geolocation is not supported by this browser.");
            return;
        }

        navigator.geolocation.getCurrentPosition(

            async (position) => {

                try {

                    const { latitude, longitude } = position.coords;

                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/weather?lat=${latitude}&lon=${longitude}`
                    );

                    const result = await response.json();

                    if (result.success) {
                        setWeather(result.data);
                    }

                } catch (err) {

                    console.error("Weather fetch error:", err);

                }

            },

            (error) => {
                console.error("Location permission/error:", error);
            }

        );
     }, []);
    // ---------------- Power flow calculations ----------------
    const pvPower = device?.pv_w || 0;
    const pvVoltage = device?.pv_v || 0;
    const pvCurrent = device?.pv_i ?? 0;

    const outputVoltage = device?.out_v || 0;
    const loadPower = device?.out_ap || 0;
    const loadCurrent = device?.load_i || 0;
    const loadPer = device?.load_pct || 0;

    const gridVoltage = device?.grid_v || 0;
    const gridCurrent = device?.grid_i || 0;
    const gridFrequency = device?.grid_f || 0;
    const outputFrequency= device?.out_f||0;
    // Positive = importing from grid, negative = exporting, 0 = idle / off-grid
    const gridPower = device?.grid_w !== undefined
  ? device.grid_w
  : (device?.grid_conn ? Math.max(loadPower - pvPower, 0) : 0);

    // Firmware only publishes bat_soc/bat_i when a battery is
    // actually connected (voltage above its "is one there at all"
    // threshold) - so a batteryless device simply never sends these
    // fields, and batterySOC naturally stays null here, which is
    // exactly what EnergyFlow.jsx's "Not Available" state checks for.
    const batterySOC = device?.bat_soc ?? null;
    const batteryVoltage = device?.bat_v ?? 0;
    const batteryCurrent = device?.bat_i ?? 0;

    // Positive = charging, negative = discharging - matches bat_i's
    // sign convention from the firmware (chargeCurrent - dischargeCurrent).
    const batteryPower = batteryVoltage && batteryCurrent
        ? Math.round(batteryVoltage * batteryCurrent)
        : 0;

    const temperature = device?.hs_t || 0;
    const rssi = device?.rssi || 0;
    const bars = signalBars(rssi);

    // ---------------- No device registered yet ----------------
    if (!checkingDevices && !activeSerial) {

        return (

            <div className="layout">

                <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

                <div className="content">

                    <Topbar setMenuOpen={setMenuOpen} title="Dashboard" subtitle="No device connected" />

                    <div className="dashboardEmptyState">
                        <FaMicrochip />
                        <h2>No device connected yet</h2>
                        <p>Add your inverter's Device ID to start seeing live data here.</p>
                        <Link to="/devices" className="dashboardEmptyBtn">
                            Add Device
                        </Link>
                    </div>

                </div>

            </div>

        );

    }

    return (

        <div className="layout">

            <Sidebar
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
            />

            <div className="content">

                <Topbar
                    deviceStatus={deviceStatus}
                    setMenuOpen={setMenuOpen}
                    serial={activeSerial}
                />

                <div className="dashboardBody">
                    {/* ---------- Weather ---------- */}
                    <div className="weatherCard">

    <div className="weatherHeader">

        <div className="weatherTitle">
            <FaThermometerHalf />
            <span>Weather</span>
        </div>

        <span className="weatherLocation">
            {weather?.location?.region || weather?.location?.name || "Current Location"}
        </span>

    </div>

    <div className="weatherMain">

        <div>
            <div className="weatherCondition">
                {weather?.current?.condition || "--"}
            </div>

            <div className="weatherTemperature">
                {weather?.current?.temperature_c ?? "--"}°C
            </div>
        </div>

    </div>

    <div className="weatherStats">

        <div className="weatherStat">
            <span className="weatherStatLabel"><FaTint />Humidity</span>
            <span className="weatherStatValue">
                {weather?.current?.humidity ?? "--"}%
            </span>
        </div>

        <div className="weatherStat">
            <span className="weatherStatLabel"><FaWind />Wind</span>
            <span className="weatherStatValue">
                {weather?.current?.wind_kph ?? "--"} km/h
            </span>
        </div>

        <div className="weatherStat">
            <span className="weatherStatLabel"><FaSun />UV Index</span>
            <span className="weatherStatValue">
                {weather?.current?.uv ?? "--"}
            </span>
        </div>

        <div className="weatherStat">
            <span className="weatherStatLabel">Condition</span>
            <span className="weatherStatValue">
                {weather?.current?.condition || "--"}
            </span>
        </div>

    </div>

</div>
                    {/* ---------- Energy flow diagram ---------- */}
                    <div className="energySection">

                        <EnergyFlow
                            pvPower={pvPower}
                            pvVoltage={pvVoltage}
                            pvCurrent={pvCurrent}
                            loadPower={loadPower}
                            loadPer={loadPer}
                            loadCurrent={loadCurrent}
                            outputVoltage={outputVoltage}
                            outputFrequency={outputFrequency}
                            batterySOC={batterySOC}
                            batteryPower={batteryPower}
                            batteryVoltage={batteryVoltage}
                            batteryCurrent={batteryCurrent}
                            gridPower={gridPower}
                            gridVoltage={gridVoltage}
                            gridCurrent={gridCurrent}
                            gridFrequency={gridFrequency}
                            peakPV={todayStats?.peak_pv ?? 0}
                            device={device}
                        />

                    </div>

                    {/* ---------- Bottom status panel (mobile responsive) ---------- */}
                    <div className="statusPanel">

                        <div className="statusHeader">

                            <div className="statusHeaderLeft">
                                <FaThermometerHalf />
                                <span>{temperature}°C</span>
                            </div>

                            <span
                                className={
                                    deviceStatus === "Online"
                                        ? "statusPill online"
                                        : "statusPill offline"
                                }
                            >
                                {deviceStatus}
                            </span>

                        </div>

                        <div className="statusList">

                            <div className="statusRow">
                                <span><FaSolarPanel /> Peak PV Power</span>
                                <strong>{todayStats.peak_pv} W</strong>
                            </div>

                            <div className="statusRow">
                                <span><FaHome /> Peak Load</span>
                                <strong>{(todayStats.peak_load / 1000).toFixed(2)} kW</strong>
                            </div>

                            <div className="statusRow">
                                <span><FaPlug /> Grid Energy Today</span>
                                <strong>{Number(todayStats.grid_energy_kwh || 0).toFixed(2)} kWh</strong>
                            </div>

                            <div className="statusRow">
                                <span><FaBolt /> Solar Energy Today</span>
                                <strong>{Number(todayStats.solar_energy_kwh || 0).toFixed(2)} kWh</strong>
                            </div>

                            <div className="statusRow">

                                <span><FaWifi /> WiFi Signal</span>

                                <span className="signalCell">

                                    <span className="signalMeter">
                                        {[1, 2, 3, 4].map(i => (
                                            <i
                                                key={i}
                                                className={i <= bars ? "bar filled" : "bar"}
                                            />
                                        ))}
                                    </span>

                                    <strong>{rssi} dBm</strong>

                                </span>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}
